import React, { useState, useEffect } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { useBooking } from '../../contexts/booking/BookingContext';
import { formatPrice } from '../../utils/bookingUtils';
import { supabase } from '../../lib/supabaseClient';

const PaymentForm: React.FC = () => {
  const { bookingState, prevStep, nextStep, getTotalPrice, setPaymentIntent, setPaymentStatus } = useBooking();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  
  const stripe = useStripe();
  const elements = useElements();
  const totalAmount = getTotalPrice();
  
  // Call the Edge Function to create a payment intent when component mounts
  useEffect(() => {
    const createPaymentIntent = async () => {
      try {
        setIsProcessing(true);

        // Get the current session token for authorization
        const { data: { session } } = await supabase.auth.getSession();
        const accessToken = session?.access_token;

        if (!accessToken) {
          console.error('No access token available for authorization');
          setErrorMessage('Authorization error. Please try again or refresh the page.');
          return;
        }

        console.log('Creating payment intent for appointment:', bookingState.appointmentId);

        const response = await fetch(
          'https://gznwnelgsrvyiyxnsnyc.supabase.co/functions/v1/create-payment-intent',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify({
              amount: totalAmount,
              appointment_id: bookingState.appointmentId,
              services: bookingState.selectedServices.map(service => ({
                id: service.id,
                name: service.name
              })),
            }),
          }
        );

        console.log('Payment intent response status:', response.status);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Error response:', errorText);
          let errorMessage;
          try {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.error?.message || `Error ${response.status}`;
          } catch (e) {
            errorMessage = `Error ${response.status}: ${errorText}`;
          }
          throw new Error(errorMessage);
        }

        const responseData = await response.json();
        console.log('Response data:', responseData);

        const { clientSecret } = responseData;
        if (!clientSecret) {
          throw new Error('No client secret returned from server');
        }

        console.log('Successfully received client secret');
        setClientSecret(clientSecret);
        setPaymentStatus('pending');
      } catch (error) {
        console.error('Error creating payment intent:', error);
        // Don't show error - Pay Later option is available
        console.log('Payment gateway unavailable. Pay Later option available.');
      } finally {
        setIsProcessing(false);
      }
    };

    if (bookingState.appointmentId) {
      createPaymentIntent();
    } else {
      setErrorMessage('Booking information is incomplete. Please go back and review your booking details.');
    }
  }, [bookingState.appointmentId, bookingState.selectedServices, setPaymentStatus, totalAmount]);

  // Handle Pay Later functionality
  const handlePayLater = async () => {
    setIsProcessing(true);
    setErrorMessage(null);

    try {
      // Update appointment payment status to pending_payment
      const { error: updateError } = await supabase
        .from('appointments')
        .update({ payment_status: 'pending_payment' })
        .eq('id', bookingState.appointmentId);

      if (updateError) {
        console.error('Error updating appointment payment status:', updateError);
      } else {
        console.log('Appointment marked as pending payment');
      }

      // Set payment status to pending and move to confirmation
      setPaymentStatus('pending');
      setPaymentIntent('pay_later');

      // Move to confirmation step
      nextStep();
    } catch (err: any) {
      console.error('Pay Later error:', err);
      setErrorMessage(err.message || 'An unexpected error occurred');
    } finally {
      setIsProcessing(false);
    }
  };
  
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!stripe || !elements || !clientSecret) {
      return;
    }
    
    setIsProcessing(true);
    setErrorMessage(null);
    
    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) throw new Error('Card element not found');
      
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: `${bookingState.customerInfo?.firstName} ${bookingState.customerInfo?.lastName}`,
            email: bookingState.customerInfo?.email,
          },
        },
      });
      
            if (error) {
        setErrorMessage(error.message || 'Payment failed');
        setPaymentStatus('failed');
      } else if (paymentIntent.status === 'succeeded') {
        // Store the payment ID in the booking context
        setPaymentIntent(paymentIntent.id);
        setPaymentStatus('succeeded');
        
        // Confirm payment on the backend
        try {
          // Get the auth token
          const { data: { session } } = await supabase.auth.getSession();
          const accessToken = session?.access_token;
          
          if (!accessToken) {
            console.error('No access token available for confirmation');
            // Continue to confirmation page even if token is missing
          } else {
            console.log('Confirming payment with appointment ID:', bookingState.appointmentId);
            
            const confirmResponse = await fetch(
              'https://gznwnelgsrvyiyxnsnyc.supabase.co/functions/v1/Confirm-Payment-Function',
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify({
                  payment_intent_id: paymentIntent.id,
                  appointment_id: bookingState.appointmentId,
                }),
              }
            );
            
            if (!confirmResponse.ok) {
              const errorText = await confirmResponse.text();
              console.warn('Payment confirmation API call failed:', errorText);
              console.warn('The webhook will handle the payment status update.');
            } else {
              console.log('Payment confirmation successful');
            }
          }
        } catch (confirmError) {
          console.error('Error confirming payment:', confirmError);
          // Continue to confirmation page even if API call fails, as payment was successful
        }
        
        // Move to confirmation step
        nextStep();
      } else {
        // Payment is processing or requires additional actions
        setPaymentStatus('processing');
        setErrorMessage('Payment is being processed. Please wait or check your email for confirmation.');
      }
    } catch (err: any) {
      console.error('Payment error:', err);
      setErrorMessage(err.message || 'An unexpected error occurred');
      setPaymentStatus('failed');
    } finally {
      setIsProcessing(false);
    }
  };
  
  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
    hidePostalCode: true,
  };
  
  return (
    <div>
      <h2 className="text-2xl font-serif text-[#232347] mb-2">Payment</h2>
      <p className="text-gray-600 mb-6">Please enter your payment details to complete your booking</p>
      
      {/* Payment summary */}
      <div className="bg-[#F1F7F4] p-4 rounded-md mb-6">
        <h3 className="font-medium text-[#232347] mb-2">Payment Summary</h3>
        <div className="flex justify-between font-medium">
          <span>Total Amount:</span>
          <span>${formatPrice(totalAmount)}</span>
        </div>
      </div>
      
      {/* Payment form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-[#232347] mb-2">Card Details</label>
          <div className="border border-gray-300 p-4 rounded-md bg-white">
            <CardElement options={cardElementOptions} />
          </div>
          <p className="text-sm text-gray-500 mt-2">
            Test Card: 4242 4242 4242 4242 | Exp: Any future date | CVC: Any 3 digits
          </p>
        </div>
        
        {errorMessage && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 p-3 rounded-md">
            {errorMessage}
          </div>
        )}

        {/* Information banner for Pay Later option */}
        <div className="mb-4 bg-blue-50 border border-blue-200 text-blue-700 p-3 rounded-md">
          <p className="text-sm">
            <strong>Payment Options:</strong> You can pay now with a card or choose "Pay Later" to complete payment at the spa.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row justify-between gap-3 mt-8">
          <button
            type="button"
            onClick={prevStep}
            disabled={isProcessing}
            className="px-6 py-2 rounded-md border border-[#B2D8C5] text-[#232347] hover:bg-[#F1F7F4] transition-colors disabled:opacity-50"
          >
            Back
          </button>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={handlePayLater}
              disabled={isProcessing}
              className="px-6 py-2 rounded-md border-2 border-[#3A5E63] text-[#3A5E63] hover:bg-[#3A5E63] hover:text-white transition-colors disabled:opacity-50 flex items-center justify-center"
            >
              {isProcessing ? (
                <>
                  <span className="animate-spin h-4 w-4 border-2 border-[#3A5E63] border-t-transparent rounded-full mr-2"></span>
                  Processing...
                </>
              ) : (
                'Pay Later'
              )}
            </button>

            <button
              type="submit"
              disabled={!stripe || !elements || isProcessing || !clientSecret}
              className="px-6 py-2 rounded-md text-white bg-[#B2D8C5] hover:bg-[#88bfa3] transition-colors disabled:opacity-50 flex items-center justify-center"
            >
              {isProcessing ? (
                <>
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                  Processing...
                </>
              ) : (
                'Pay Now'
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PaymentForm;